// src/pages/InventoryListView.js
import React, { useState, useEffect, useMemo, useContext } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
    FiSearch, FiPlus, FiAlertTriangle, 
    FiChevronLeft, FiChevronRight, FiX, FiClock, FiPackage, 
    FiLogIn, FiLogOut, FiArrowLeft, FiHash, FiCheck, FiChevronDown,
    FiSettings, FiShoppingCart, FiMinusCircle, FiMapPin,
    FiCamera, FiTrash2, FiLayers, FiGrid, FiArrowRight
} from 'react-icons/fi';
import { QrReader } from 'react-qr-reader';
import * as XLSX from 'xlsx';

import InventoryHistoryModal from '../components/InventoryHistoryModal';
import InventoryUnitsModal from '../components/InventoryUnitsModal'; 
import UnitHistoryModal from '../components/UnitHistoryModal';
import Modal from '../components/Modal'; 
import { useApi } from '../hooks/useApi';
import { useIsMobile } from '../hooks/useIsMobile';
import { AuthContext } from '../context/AuthContext';
import { useModal } from '../context/ModalContext';

// =================================================================================
// QR SCANNER MODAL
// =================================================================================
const ScanQRModal = ({ isOpen, onClose, onScan }) => {
    if (!isOpen) return null;
    return (
        <div className="modal-overlay" style={{ zIndex: 2000 }} onClick={onClose}>
            <div className="modal-content modal-content-slim" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <h3>Scan Item QR</h3>
                    <button onClick={onClose} className="modal-close-btn"><FiX /></button>
                </div>
                <div className="modal-body" style={{ padding: '0', background: 'black', position: 'relative', minHeight: '300px' }}>
                    <QrReader
                        onResult={(result) => { if (result) onScan(result?.text); }}
                        constraints={{ facingMode: 'environment' }}
                        containerStyle={{ width: '100%', height: '100%' }}
                        videoStyle={{ objectFit: 'cover' }}
                    />
                    <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: '200px', height: '200px', border: '2px solid rgba(255,255,255,0.7)', borderRadius: '12px', boxShadow: '0 0 0 9999px rgba(0,0,0,0.5)', pointerEvents: 'none' }}></div>
                </div>
                <div className="modal-footer"><button className="btn btn-secondary btn-block" onClick={onClose}>Cancel</button></div>
            </div>
        </div>
    );
};

// =================================================================================
// STEP 1: PICK MASTER ITEM
// =================================================================================
const ItemSelectModal = ({ isOpen, onClose, items, onSelect, title = "Select Item" }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const filteredItems = useMemo(() => {
        if (!items) return [];
        const lower = searchTerm.toLowerCase();
        return items.filter(i => i.name.toLowerCase().includes(lower) || (i.sku && i.sku.toLowerCase().includes(lower)) || (i.category && i.category.toLowerCase().includes(lower)));
    }, [items, searchTerm]);

    if (!isOpen) return null;
    return (
        <Modal isOpen={isOpen} onClose={onClose} title={title}>
            <div className="search-input-wrapper modal-search-wrapper" style={{ marginBottom: '1rem' }}><FiSearch className="search-icon" /><input type="search" className="search-input" placeholder="Search by name, SKU, or category..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} autoFocus /></div>
            <div className="selection-list" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                {filteredItems.map(item => (
                    <div key={item._id} className="selection-list-item" onClick={() => onSelect(item)} style={{ justifyContent: 'flex-start' }}>
                        <div className="list-view-image-thumbnail-wrapper" style={{ marginRight: '1rem', width: '40px', height: '40px', flexShrink: 0 }}>
                            {item.imageUrl ? <img src={item.imageUrl} alt={item.name} className="list-view-image-thumbnail" /> : <FiPackage size={20} color="var(--gray-400)" />}
                        </div>
                        <div className="item-main-info" style={{ flexGrow: 1 }}><p className="item-title">{item.name}</p><p className="item-subtitle">{item.sku ? `SKU: ${item.sku} • ` : ''}{item.category}{item.quantity !== undefined ? ` • In Stock: ${item.quantity}` : ''}</p></div>
                        <FiChevronRight />
                    </div>
                ))}
            </div>
            <div className="modal-footer"><button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button></div>
        </Modal>
    );
};

// =================================================================================
// STEP 2b: PICK SPECIFIC UNITS (If Component is Serialized)
// =================================================================================
const UnitSelectModal = ({ isOpen, onClose, units, onSelect }) => {
    const [selectedIds, setSelectedIds] = useState([]);
    if (!isOpen) return null;
    return (
        <Modal isOpen={isOpen} onClose={onClose} title="Select Specific Units">
            <p className="form-group-description">This item is serialized. Pick the specific units to put inside this assembly.</p>
            <div className="selection-list" style={{ maxHeight: '350px', overflowY: 'auto' }}>
                {units.map(unit => {
                    const isSelected = selectedIds.includes(unit._id);
                    return (
                        <div key={unit._id} className={`selection-list-item ${isSelected ? 'selected' : ''}`} onClick={() => setSelectedIds(prev => isSelected ? prev.filter(id => id !== unit._id) : [...prev, unit._id])}>
                            <div className="item-main-info"><p className="item-title">SN: {unit.serialNumber}</p></div>{isSelected && <FiCheck className="selection-checkmark" />}
                        </div>
                    );
                })}
            </div>
            <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
                <button className="btn btn-primary" onClick={() => { onSelect(selectedIds); setSelectedIds([]); }} disabled={selectedIds.length === 0}>Confirm {selectedIds.length} Units</button>
            </div>
        </Modal>
    );
};

// =================================================================================
// MAIN WIZARD UI: ADD ITEM MODAL
// =================================================================================
const AddItemModal = ({ isOpen, onClose, onAddItem, masterInventoryItems, allItems, ownerId, ownerType }) => {
    // Wizard State
    const [step, setStep] = useState('START'); // START, PICK_MASTER, PICK_COMP, PICK_UNIT
    const [selectedMaster, setSelectedMaster] = useState(null);
    const [quantity, setQuantity] = useState(1);
    const [serialNumbers, setSerialNumbers] = useState(['']); 
    
    // Assembly Builder State
    const [components, setComponents] = useState([]); 
    const [tempPart, setTempPart] = useState(null);
    const [availableUnits, setAvailableUnits] = useState([]);

    const callApi = useApi();
    const { showAlert, showConfirm } = useModal();

    const localInventory = useMemo(() => allItems.filter(i => {
        if (i.ownerId !== ownerId || i.quantity <= 0) return false;
        
        // Filter out other assemblies so they don't appear in the component pick list
        const masterDef = masterInventoryItems?.find(m => m._id === i.masterInventoryItemId);
        const isAssemblyType = i.isAssembly || masterDef?.itemType === 'Assembly';
        
        return !isAssemblyType;
    }), [allItems, ownerId, masterInventoryItems]);
    
    const isHighValue = selectedMaster?.itemType === 'High Value Equipment';

    useEffect(() => {
        if (isOpen) {
            setStep('START');
            setSelectedMaster(null);
            setQuantity(1);
            setSerialNumbers(['']);
            setComponents([]);
        }
    }, [isOpen]);

    const handleMasterSelected = (master) => {
        setSelectedMaster(master);
        if (master.itemType === 'Assembly') {
            setStep('START'); // Show main form, ready to pick components
        } else {
            setStep('START'); // Show standard form
        }
    };

    // ✅ FIXED: Allow input to be cleared to avoid glitchy re-typing 
    const handleQuantityChange = (e) => {
        const val = e.target.value;

        // Allow the input to be completely cleared out
        if (val === '') {
            setQuantity('');
            if (isHighValue) {
                setSerialNumbers([]);
            }
            return;
        }

        const parsedVal = parseInt(val, 10);
        
        if (!isNaN(parsedVal)) {
            setQuantity(parsedVal);
            if (isHighValue) {
                setSerialNumbers(prev => {
                    if (parsedVal > prev.length) return [...prev, ...Array(parsedVal - prev.length).fill('')];
                    return prev.slice(0, parsedVal);
                });
            }
        }
    };

    const handleSnChange = (index, value) => {
        const newSns = [...serialNumbers];
        newSns[index] = value;
        setSerialNumbers(newSns);
    };

    const handlePartSelected = async (part) => {
        const masterDef = masterInventoryItems.find(m => m._id === part.masterInventoryItemId);
        
        // --- HIGH VALUE / SERIALIZED ITEMS ---
        if (masterDef?.itemType === 'High Value Equipment' || masterDef?.itemType === 'Assembly') {
            const res = await callApi(`inventory/${part._id}/units`);
            if (res.success && Array.isArray(res.data)) {
                // Exclude units that are already staged in the builder state
                const alreadySelectedUnitIds = components.flatMap(c => c.unitIds || []);
                
                const availableForSelection = res.data.filter(u => 
                    u.status === 'Available' && 
                    u.ownerId === part.ownerId &&
                    !alreadySelectedUnitIds.includes(u._id)
                );

                if (availableForSelection.length === 0) {
                    showAlert("There are no more available units for this item.", "Out of Stock");
                    return;
                }

                setAvailableUnits(availableForSelection); 
                setTempPart(part); 
                setStep('PICK_UNIT'); 
                
            } else {
                showAlert(res.error || "Failed to fetch available units.");
            }
        } 
        // --- BULK / CONSUMABLE ITEMS ---
        else {
            // Account for bulk quantities already staged in the builder
            const alreadyAssignedQty = components
                .filter(c => c.inventoryId === part._id)
                .reduce((sum, c) => sum + c.quantity, 0);
                
            const maxAvailable = part.quantity - alreadyAssignedQty;

            if (maxAvailable <= 0) {
                showAlert("You have already assigned all available stock of this item to the assembly.", "Out of Stock");
                return;
            }

            const qtyStr = prompt(`Quantity of ${part.name} to include? (Available: ${maxAvailable})`, "1");
            if (qtyStr === null) return; 
            
            const qty = parseInt(qtyStr, 10);
            
            if (qty > 0 && qty <= maxAvailable) {
                setComponents(prev => [...prev, { inventoryId: part._id, name: part.name, quantity: qty, unitIds: [] }]);
                setStep('START'); // Back to main overview
            } else {
                showAlert(`Invalid quantity. You can only add up to ${maxAvailable}.`);
            }
        }
    };

    const handleUnitsConfirmed = (unitIds) => {
        if (!tempPart || unitIds.length === 0) return;

        const serials = availableUnits.filter(u => unitIds.includes(u._id)).map(u => u.serialNumber);
        setComponents(prev => [...prev, { inventoryId: tempPart._id, name: tempPart.name, quantity: unitIds.length, unitIds: unitIds, serials: serials }]);
        setTempPart(null);
        setStep('START'); // Back to main overview
    };

    // ✅ FIXED: Safely handle if 'quantity' state is currently an empty string
    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Safety check to ensure an empty field defaults to at least 1 upon submission
        const finalQuantity = parseInt(quantity, 10) || 1;

        if (isHighValue) {
            if (serialNumbers.some(sn => !sn.trim())) return showAlert("Please provide Serial Numbers for all items.");
            const unique = new Set(serialNumbers);
            if (unique.size !== serialNumbers.length) return showAlert("Duplicate Serial Numbers entered in this batch.", "Validation Error");
        }
        
        const payload = { 
            masterInventoryItemId: selectedMaster._id, 
            quantity: selectedMaster.itemType === 'Assembly' ? 1 : finalQuantity, 
            ownerId, ownerType, 
            name: selectedMaster.name, 
            serialNumbers: isHighValue ? serialNumbers : undefined, 
            components 
        };
        
        const result = await onAddItem(payload);
        if (result?.success) {
            showConfirm("Successfully added to stock. Export MakeID Labels?", () => {
                const { addedAggregateId, addedUnits } = result.data;
                const baseUrl = window.location.origin; 
                const rows = [];
                if ((selectedMaster.itemType === 'High Value Equipment' || selectedMaster.itemType === 'Assembly') && addedUnits?.length > 0) {
                    addedUnits.forEach(unit => rows.push({ "Item Name": payload.name, "Serial Number": unit.serialNumber || 'N/A', "QR Link": `${baseUrl}/inventory/checkout-unit/${unit._id}`, "Date Added": new Date().toLocaleString() }));
                } else {
                    for (let i = 0; i < finalQuantity; i++) rows.push({ "Item Name": payload.name, "Serial Number": "N/A", "QR Link": `${baseUrl}/inventory/checkout/${addedAggregateId}`, "Date Added": new Date().toLocaleString() });
                }
                const worksheet = XLSX.utils.json_to_sheet(rows);
                const workbook = XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(workbook, worksheet, "Labels");
                XLSX.writeFile(workbook, `Labels_${payload.name.replace(/ /g, '_')}.xlsx`);
            }, "Export Labels", false, "Yes, Export", "No, Thanks");
        }
        onClose();
    };

    if (!isOpen) return null;

    return (
        <>
            {/* Step 1: Main Form (Displayed only when step is 'START') */}
            {step === 'START' && (
                <Modal isOpen={true} onClose={onClose} title="Add Stock" size={selectedMaster?.itemType === 'Assembly' ? 'wide' : 'standard'}>
                    <form onSubmit={handleSubmit}>
                        <div className="form-group">
                            <label>Select Master Definition</label>
                            <div className="contact-display-box" onClick={() => setStep('PICK_MASTER')} style={{ cursor: 'pointer' }}>
                                {selectedMaster ? (
                                    <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}><span>{selectedMaster.name}</span><span className="tag">{selectedMaster.itemType}</span></div>
                                ) : <span style={{color: 'var(--gray-500)'}}>Click to select from master catalog...</span>}
                            </div>
                        </div>

                        {selectedMaster?.itemType === 'Assembly' && (
                            <div style={{ backgroundColor: 'var(--gray-50)', padding: '1.25rem', borderRadius: '8px', border: '1px solid var(--gray-200)', marginBottom: '1.5rem' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                                    <h4 style={{ margin: 0, color: 'var(--gray-800)' }}><FiLayers /> Assembly Builder</h4>
                                    <button type="button" className="btn btn-secondary btn-sm" onClick={() => setStep('PICK_COMP')}><FiPlus /> Add Component</button>
                                </div>
                                {components.length > 0 ? (
                                    <div className="items-list" style={{ backgroundColor: 'var(--white)', borderRadius: '6px', border: '1px solid var(--gray-200)' }}>
                                        {components.map((c, idx) => (
                                            <div key={idx} className="list-item-row" style={{ borderBottom: '1px solid var(--gray-100)' }}>
                                                <div className="item-info">
                                                    <span className="item-title">{c.name}</span>
                                                    <span className="item-subtitle">Qty: {c.quantity} {c.serials?.length > 0 ? `(SN: ${c.serials.join(', ')})` : ''}</span>
                                                </div>
                                                <button type="button" className="btn-icon-only btn-icon-danger" onClick={() => setComponents(p => p.filter((_, i) => i !== idx))}><FiTrash2 /></button>
                                            </div>
                                        ))}
                                    </div>
                                ) : <p style={{ textAlign: 'center', fontSize: '0.85rem', color: 'var(--gray-500)', margin: '1rem 0' }}>Assembly is empty. Click "Add Component" to build it.</p>}
                            </div>
                        )}

                        {/* ✅ FIXED: Hide Quantity completely if it's an Assembly, add required constraint */}
                        {selectedMaster?.itemType !== 'Assembly' && (
                            <div className="form-group">
                                <label>Stock Quantity</label>
                                <input 
                                    type="number" 
                                    className="form-input" 
                                    value={quantity} 
                                    onChange={handleQuantityChange} 
                                    min="1" 
                                    required 
                                />
                            </div>
                        )}

                        {isHighValue && quantity > 0 && (
                            <div className="form-group" style={{ backgroundColor: 'var(--gray-50)', padding: '1rem', borderRadius: '8px', border: '1px solid var(--gray-200)' }}>
                                <label style={{ color: 'var(--gray-800)', display:'flex', alignItems:'center', gap:'0.5rem' }}><FiHash /> Enter Serial Numbers</label>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', maxHeight: '150px', overflowY: 'auto', paddingRight: '0.5rem' }}>
                                    {serialNumbers.map((sn, idx) => (
                                        <input key={idx} type="text" placeholder={`S/N for Item #${idx + 1}`} value={sn} onChange={(e) => { const n=[...serialNumbers]; n[idx]=e.target.value; setSerialNumbers(n); }} required className="form-input" style={{fontSize: '0.9rem'}} />
                                    ))}
                                </div>
                            </div>
                        )}

                        <div className="modal-footer"><button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button><button type="submit" className="btn btn-primary" disabled={!selectedMaster}>Confirm Stock Addition</button></div>
                    </form>
                </Modal>
            )}
            
            {/* Step 2a: Pick Master Item */}
            <ItemSelectModal isOpen={step === 'PICK_MASTER'} onClose={() => setStep('START')} items={masterInventoryItems} onSelect={handleMasterSelected} title="Select Master Catalog Item" />
            
            {/* Step 2b: Pick Components (for Assembly) */}
            <ItemSelectModal isOpen={step === 'PICK_COMP'} onClose={() => setStep('START')} items={localInventory} onSelect={handlePartSelected} title="Pick Component from Local Stock" />
            
            {/* Step 2c: Pick Units (for Serialized Components) */}
            <UnitSelectModal isOpen={step === 'PICK_UNIT'} onClose={() => setStep('START')} units={availableUnits} onSelect={handleUnitsConfirmed} />
        </>
    );
};

// =================================================================================
// Adjust Stock Modal 
// =================================================================================
const AdjustStockModal = ({ isOpen, onClose, onAdjustStock, items, initialItemSku }) => {
    const [selectedItemId, setSelectedItemId] = useState('');
    const [newQuantity, setNewQuantity] = useState('');

    useEffect(() => {
        if (isOpen) {
            const defaultId = (initialItemSku && items.some(i => i.sku === initialItemSku || i._id === initialItemSku)) 
                ? (items.find(i => i.sku === initialItemSku || i._id === initialItemSku)?._id || '') 
                : (items.length > 0 ? items[0]._id : '');
            setSelectedItemId(defaultId);
        }
    }, [isOpen, items, initialItemSku]);

    useEffect(() => {
        if (selectedItemId) {
            const item = items.find(i => i._id === selectedItemId);
            setNewQuantity(item ? String(item.quantity) : '');
        } else {
            setNewQuantity('');
        }
    }, [selectedItemId, items]);

    if (!isOpen) return null;

    const handleSubmit = (e) => {
        e.preventDefault();
        const itemToUpdate = items.find(item => item._id === selectedItemId);
        if (itemToUpdate) {
            onAdjustStock(itemToUpdate._id, { quantity: parseInt(newQuantity, 10), lastUpdated: new Date().toISOString().split('T')[0] });
        }
        onClose();
    };

    return (
      <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header"><h3>Adjust Stock</h3><button onClick={onClose} className="modal-close-btn"><FiX /></button></div>
            <form onSubmit={handleSubmit} className="modal-form">
              <div className="form-group"><label>Item</label><select className="form-select" value={selectedItemId} onChange={(e) => setSelectedItemId(e.target.value)}>{items.map(item => (<option key={item._id} value={item._id}>{item.name} ({item.sku || 'N/A'})</option>))}</select></div>
              <div className="form-group"><label>New Quantity</label><input type="number" className="form-input" min="0" value={newQuantity} onChange={(e) => setNewQuantity(e.target.value)} required /></div>
              <div className="modal-footer"><button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button><button type="submit" className="btn btn-primary">Update Quantity</button></div>
             </form>
          </div>
      </div>
    );
};

// =================================================================================
// Inventory Action Modal
// =================================================================================
const InventoryActionModal = ({ isOpen, onClose, item, sites, communities, tickets, onAdjustStock, onAddItem, onTransferStock, onConsumeItem, initialMode = 'menu' }) => {
    const [mode, setMode] = useState(initialMode); 
    const [checkoutQty, setCheckoutQty] = useState(1);
    const [destId, setDestId] = useState('');
    const [addQuantity, setAddQuantity] = useState(1);
    const [checkinSerialNumbers, setCheckinSerialNumbers] = useState([]);
    const [consumeQty, setConsumeQty] = useState(1);
    const [availableDestinations, setAvailableDestinations] = useState([]);
    const isHighValue = item?.itemType === 'High Value Equipment';

    useEffect(() => {
        if (isOpen && item) {
            setMode(initialMode); setCheckoutQty(1); setAddQuantity(1); setConsumeQty(1); setCheckinSerialNumbers(isHighValue ? [''] : []); setDestId('');
            const sourceSite = sites ? sites.find(s => s._id === item.ownerId) : null;
            let dests = [];
            if (item.ownerType === 'Site') {
                if (sourceSite?.type === 'Office') dests = (sites || []).filter(s => s.type === 'Truck').map(s => ({ _id: s._id, name: s.name, type: 'Site', label: `Truck: ${s.name}` }));
                else if (sourceSite?.type === 'Truck') {
                    const offices = (sites || []).filter(s => s.type === 'Office').map(s => ({ _id: s._id, name: s.name, type: 'Site', label: `Office: ${s.name}` }));
                    const comms = (communities || []).map(c => ({ _id: c._id, name: c.name, type: 'Community', label: `Community: ${c.name}` }));
                    dests = [...offices, ...comms];
                }
            } else {
                const offices = (sites || []).filter(s => s.type === 'Office').map(s => ({ _id: s._id, name: s.name, type: 'Site', label: `Office: ${s.name}` }));
                dests = [...offices];
            }
            setAvailableDestinations(dests);
            if (dests.length > 0) setDestId(dests[0]._id);
        }
    }, [isOpen, item, sites, communities, isHighValue, initialMode]);

    if (!isOpen) return null;

    const handleAddQtyChange = (e) => {
        const val = parseInt(e.target.value, 10) || 0;
        setAddQuantity(val);
        if (isHighValue) setCheckinSerialNumbers(prev => val > prev.length ? [...prev, ...Array(val - prev.length).fill('')] : prev.slice(0, val));
    };

    const handleCheckinSnChange = (index, value) => {
        const newSn = [...checkinSerialNumbers];
        newSn[index] = value;
        setCheckinSerialNumbers(newSn);
    };

    const handleCheckinSubmit = (e) => {
        e.preventDefault();
        if (isHighValue && checkinSerialNumbers.some(sn => !sn.trim())) return alert("Please provide Serial Numbers for all new items.");
        onAddItem({ masterInventoryItemId: item.masterInventoryItemId, quantity: addQuantity, ownerId: item.ownerId, ownerType: item.ownerType, serialNumbers: isHighValue ? checkinSerialNumbers : undefined });
        onClose();
    };

    const handleCheckoutSubmit = async (e) => {
        e.preventDefault();
        if (!destId) return alert("Please select a destination.");
        if (checkoutQty > item.quantity) return alert(`Cannot transfer more than available stock (${item.quantity}).`);
        const destinationObj = availableDestinations.find(d => d._id === destId);
        const res = await onTransferStock({ sourceInventoryId: item._id, destinationSiteId: destId, quantity: checkoutQty, destinationType: destinationObj ? destinationObj.type : 'Site' });
        if (res.success) onClose();
    };

    const handleConsumeSubmit = async (e) => {
        e.preventDefault();
        if (consumeQty > item.quantity) return alert(`Cannot take more than available stock (${item.quantity}).`);
        const res = await onConsumeItem({ inventoryId: item._id, quantity: consumeQty });
        if (res.success) onClose();
    };

    const renderMenu = () => (
        <>
            <div className="modal-header"><h3>{item.name} ({item.sku})</h3><button onClick={onClose} className="modal-close-btn"><FiX /></button></div>
            <div className="modal-body" style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                <p>Current stock: <strong>{item.quantity}</strong></p>
                {isHighValue && <span className="detail-tag" style={{alignSelf:'center'}}>High Value Item (S/N Required)</span>}
                <p>Select an action:</p>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}><button className="btn btn-secondary" onClick={() => setMode('checkin')}><FiLogIn className="btn-icon" /> Check In</button><button className="btn btn-secondary" onClick={() => setMode('checkout')} disabled={availableDestinations.length === 0}><FiLogOut className="btn-icon" /> Transfer</button></div>
            </div>
            <div className="modal-footer"><button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button></div>
        </>
    );

    const renderCheckin = () => (
        <>
            <div className="modal-header"><h3>Check In Stock: {item.name}</h3><button onClick={onClose} className="modal-close-btn"><FiX /></button></div>
            <form onSubmit={handleCheckinSubmit}>
                <div className="modal-body">
                    <p>Current Quantity: <strong>{item.quantity}</strong></p>
                    <div className="form-group"><label htmlFor="add-quantity">Quantity to Add</label><input id="add-quantity" type="number" className="form-input" value={addQuantity} onChange={handleAddQtyChange} min="1" required autoFocus /></div>
                    {isHighValue && addQuantity > 0 && (
                        <div className="form-group" style={{ backgroundColor: 'var(--gray-50)', padding: '1rem', borderRadius: '8px', border: '1px solid var(--gray-200)' }}>
                            <label style={{ color: 'var(--gray-800)', display:'flex', alignItems:'center', gap:'0.5rem' }}><FiHash /> Enter Serial Numbers</label>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', maxHeight: '150px', overflowY: 'auto', paddingRight: '0.5rem' }}>
                                {checkinSerialNumbers.map((sn, idx) => ( <input key={idx} type="text" placeholder={`S/N for New Item #${idx + 1}`} value={sn} onChange={(e) => handleCheckinSnChange(idx, e.target.value)} required className="form-input" style={{fontSize: '0.9rem'}} /> ))}
                            </div>
                        </div>
                    )}
                </div>
                <div className="modal-footer"><button type="button" className="btn btn-secondary" onClick={() => setMode('menu')}>Back</button><button type="submit" className="btn btn-primary">Confirm Check-In</button></div>
            </form>
        </>
    );

    const renderCheckout = () => (
        <>
            <div className="modal-header"><h3>Transfer {item.name}</h3><button onClick={onClose} className="modal-close-btn"><FiX /></button></div>
            <form onSubmit={handleCheckoutSubmit}>
                <div className="modal-body">
                    <div className="form-group"><label htmlFor="destination">Destination</label><select id="destination" className="form-select" value={destId} onChange={(e) => setDestId(e.target.value)} required>{availableDestinations.map(dest => (<option key={dest._id} value={dest._id}>{dest.label}</option>))}</select></div>
                    <div className="form-group"><label htmlFor="checkout-quantity">Quantity to Transfer</label><input id="checkout-quantity" type="number" className="form-input" value={checkoutQty} onChange={(e) => setCheckoutQty(parseInt(e.target.value, 10))} min="1" max={item.quantity} required autoFocus /><small style={{ marginTop: '0.25rem', display: 'block' }}>Available: {item.quantity}</small></div>
                </div>
                <div className="modal-footer">{initialMode !== 'checkout' && ( <button type="button" className="btn btn-secondary" onClick={() => setMode('menu')}>Back</button> )}<button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button><button type="submit" className="btn btn-primary">Confirm Transfer</button></div>
            </form>
        </>
    );

    const renderConsume = () => (
        <>
            <div className="modal-header"><h3>Take {item.name}</h3><button onClick={onClose} className="modal-close-btn"><FiX /></button></div>
            <form onSubmit={handleConsumeSubmit}>
                <div className="modal-body">
                    <p style={{marginBottom: '1rem', color: 'var(--gray-600)'}}>Use this to log everyday items taken from stock (e.g. zip ties) that don't need to be billed to a specific ticket.</p>
                    <div className="form-group"><label htmlFor="consume-quantity">Quantity Taken</label><input id="consume-quantity" type="number" className="form-input" value={consumeQty} onChange={(e) => setConsumeQty(parseInt(e.target.value, 10))} min="1" max={item.quantity} required autoFocus /><small style={{ marginTop: '0.25rem', display: 'block' }}>Available: {item.quantity}</small></div>
                </div>
                <div className="modal-footer"><button type="button" className="btn btn-secondary" onClick={() => setMode('menu')}>Back</button><button type="submit" className="btn btn-primary">Confirm Usage</button></div>
            </form>
        </>
    );

    return <div className="modal-overlay"><div className="modal-content modal-content-slim">{mode === 'menu' && renderMenu()}{mode === 'checkin' && renderCheckin()}{mode === 'checkout' && renderCheckout()}{mode === 'consume' && renderConsume()}</div></div>;
};

// =================================================================================
// INVENTORY DETAILS MODAL
// =================================================================================
const InventoryDetailsModal = ({ isOpen, onClose, item, onAction, onHistory, onAdjust, onDelete, canDelete, onViewSubItemUnits }) => {
    const navigate = useNavigate();
    if (!isOpen || !item) return null;
    const isAssembly = item.itemType === 'Assembly' || item.isAssembly;

    return (
        <Modal isOpen={isOpen} onClose={onClose} title={item.name}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', alignItems: 'center', textAlign: 'center' }}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem' }}>
                    <div className="list-view-image-thumbnail-wrapper" style={{ width: '80px', height: '80px', borderRadius: '12px' }}>
                        {item.imageUrl ? <img src={item.imageUrl} alt={item.name} className="list-view-image-thumbnail" /> : <FiPackage size={40} color="var(--gray-400)" />}
                    </div>
                    <div>
                        <span className="item-meta" style={{ display: 'block', fontSize: '0.9rem' }}>SKU: {item.sku || 'N/A'} • {item.category}</span>
                        <div style={{ fontSize: '2rem', fontWeight: 'bold', color: 'var(--gray-800)', marginTop: '0.5rem' }}>{item.quantity}</div>
                        <span className="item-meta" style={{ fontSize: '0.8rem', textTransform: 'uppercase' }}>Current Stock</span>
                    </div>
                </div>

                <hr className="section-divider" style={{ width: '100%', margin: '0' }} />

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', width: '100%' }}>
                    {!isAssembly && (
                        <>
                            <button className="btn btn-primary" style={{ gridColumn: '1 / -1', padding: '0.75rem' }} onClick={() => navigate(`/inventory/checkout/${item._id}`)}><FiArrowRight className="btn-icon" /> Checkout / Transfer</button>
                            <button className="btn btn-secondary" onClick={() => onAction('checkin')}><FiLogIn className="btn-icon" /> Add Stock</button>
                            <button className="btn btn-secondary" onClick={() => onAction('consume')}><FiMinusCircle className="btn-icon" /> Quick Take</button>
                        </>
                    )}
                    {isAssembly && <button className="btn btn-primary" style={{ gridColumn: '1 / -1', padding: '0.75rem' }} onClick={() => onViewSubItemUnits(item)}><FiGrid className="btn-icon" /> Manage Specific Units</button>}
                    <button className="btn btn-secondary" style={{ gridColumn: isAssembly ? '1 / -1' : 'auto' }} onClick={onHistory}><FiClock className="btn-icon" /> History</button>
                    <button className="btn btn-secondary" style={{ gridColumn: isAssembly ? '1 / -1' : 'auto' }} onClick={onAdjust}><FiSettings className="btn-icon" /> Adjust Stock</button>
                    {canDelete && <button className="btn btn-danger" style={{ gridColumn: '1 / -1', padding: '0.75rem' }} onClick={onDelete}><FiTrash2 className="btn-icon" /> Delete</button>}
                </div>
                
                {isAssembly && (
                    <div style={{ width: '100%', textAlign: 'left', marginTop: '0.5rem' }}>
                        <h4 style={{ marginBottom: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--gray-700)' }}><FiLayers /> Assembly Configuration</h4>
                        <div style={{ padding: '1rem', backgroundColor: 'var(--gray-50)', borderRadius: '8px', border: '1px solid var(--gray-200)', color: 'var(--gray-800)', fontSize: '0.9rem' }}>
                            <p style={{ margin: 0 }}>Components are tracked inside specific units. Click <strong>"Manage Specific Units"</strong> above to view or modify an assembly's contents.</p>
                        </div>
                    </div>
                )}
            </div>
        </Modal>
    );
};

// =================================================================================
// MAIN LIST COMPONENT
// =================================================================================
const InventoryListView = ({ owner, ownerType, allItems, onBack, onAddItem, onAdjustStock, onDeleteMultipleInventoryItems, masterInventoryItems, onTransferStock, sites, communities, tickets, onConsumeItem }) => {
    const isMobile = useIsMobile();
    const location = useLocation();
    const navigate = useNavigate();
    const { user } = useContext(AuthContext);
    const { showConfirm } = useModal();

    const canManageInventory = useMemo(() => user?.roles?.some(role => ['Giga Admin', 'Admin'].includes(role)), [user]);
    const canDeleteInventory = useMemo(() => user?.roles?.includes('Giga Admin'), [user]);

    const [isAddItemModalOpen, setAddItemModalOpen] = useState(false);
    const [isAdjustStockModalOpen, setAdjustStockModalOpen] = useState(false);
    const [isActionModalOpen, setIsActionModalOpen] = useState(false);
    const [actionModalMode, setActionModalMode] = useState('menu');
    const [isUnitsModalOpen, setIsUnitsModalOpen] = useState(false);
    const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
    const [isHistoryOpen, setIsHistoryOpen] = useState(false);
    const [isScannerOpen, setIsScannerOpen] = useState(false); 
    const [isUnitHistoryOpen, setIsUnitHistoryOpen] = useState(false);
    
    const [unitHistorySN, setUnitHistorySN] = useState(null);
    const [selectedItem, setSelectedItem] = useState(null);
    const [historyItem, setHistoryItem] = useState(null);
    const [itemToAdjustSku, setItemToAdjustSku] = useState(null);

    const [filteredItems, setFilteredItems] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [filters, setFilters] = useState({ category: 'All Categories', lowStockOnly: false });
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const enrichedOwnerItems = useMemo(() => {
        const sourceItems = searchTerm.length > 0 ? allItems : allItems.filter(i => i.ownerId === owner._id);
        return sourceItems.map(item => {
            const master = masterInventoryItems.find(mi => mi._id === item.masterInventoryItemId);
            return { ...item, itemType: master?.itemType || 'Misc/Consumable', imageUrl: master?.imageUrl, manufacturer: item.manufacturer || master?.manufacturer, buyAgainLink: master?.buyAgainLink };
        });
    }, [allItems, owner._id, masterInventoryItems, searchTerm]);

    useEffect(() => {
        let result = [...enrichedOwnerItems];
        
        if (owner.type === 'Truck' && !searchTerm) {
            result = result.filter(item => item.quantity > 0);
        }
        
        if (searchTerm) {
            const low = searchTerm.toLowerCase();
            result = result.filter(i => i.name.toLowerCase().includes(low) || (i.sku && i.sku.toLowerCase().includes(low)) || (i.manufacturer && i.manufacturer.toLowerCase().includes(low)));
        }
    
        if (filters.category !== 'All Categories') result = result.filter(i => i.category === filters.category);
        if (filters.lowStockOnly) result = result.filter(i => i.quantity <= i.reorderLevel);
        
        result.sort((a, b) => a.name.localeCompare(b.name));
        
        setFilteredItems(result);
        setCurrentPage(1);
    }, [enrichedOwnerItems, searchTerm, filters, owner.type]);

    useEffect(() => {
        if (location.state?.autoOpenUnitSerial) {
            setUnitHistorySN(location.state.autoOpenUnitSerial);
            setIsUnitHistoryOpen(true);
            navigate(location.pathname + location.search, { replace: true, state: {} });
        } else if (location.state?.showHistory) {
            const targetId = location.state.showHistory;
            const foundItem = enrichedOwnerItems.find(i => i._id === targetId);
            if (foundItem) {
                setHistoryItem(foundItem);
                setIsHistoryOpen(true);
                navigate(location.pathname + location.search, { replace: true, state: {} });
            }
        }
    }, [location.state, enrichedOwnerItems, navigate, location.pathname, location.search]);

    const handleItemClick = (item) => {
        setSelectedItem(item);
        if (item.itemType === 'High Value Equipment' || item.itemType === 'Assembly') setIsUnitsModalOpen(true);
        else setIsDetailsModalOpen(true);
    };

    const handleViewHistory = (e, item) => {
        if(e) e.stopPropagation();
        setHistoryItem(item);
        setIsHistoryOpen(true);
    };

    const handleDeleteSingleItem = (item) => {
        showConfirm(`Are you sure you want to permanently delete "${item.name}" and ALL of its units from this location?`, async () => {
            await onDeleteMultipleInventoryItems([item._id]);
            if (isDetailsModalOpen) setIsDetailsModalOpen(false);
        }, 'Delete Inventory Item', true);
    };

    const handleScan = (data) => {
        if (!data) return;
        let targetPath = '';
        const match = data.match(/(?:checkout|checkout-unit)\/([a-zA-Z0-9]+)/);
        if (match && match[1]) {
            targetPath = `/inventory/${data.includes('checkout-unit') ? 'checkout-unit' : 'checkout'}/${match[1]}`;
        } else {
            targetPath = `/inventory/checkout-unit/${data}`;
        }
        setIsScannerOpen(false);
        navigate(targetPath);
    };

    const renderMobileCard = (item) => {
        const isLow = owner.type === 'Office' && item.quantity <= item.reorderLevel;
        return (
            <div key={item._id} className={`mobile-inventory-card ${isLow ? 'low-stock-warning' : ''}`} onClick={() => handleItemClick(item)}>
                <div className="mobile-card-main">
                    <div className="list-view-image-thumbnail-wrapper">{item.imageUrl ? <img src={item.imageUrl} alt={item.name} className="list-view-image-thumbnail" /> : <FiPackage className="list-view-image-placeholder-icon" />}</div>
                    <div className="mobile-card-info">
                        <h4 className="mobile-item-name">{item.name}</h4>
                        {/* ✅ FIXED: Changed color from var(--blue-600) to var(--gray-500) for contrast */}
                        {item.manufacturer && <span className="mobile-item-sku" style={{display:'block', fontSize:'0.75rem', color:'var(--gray-500)'}}>{item.manufacturer}</span>}
                        <span className="mobile-item-sku">SKU: {item.sku}</span>
                    </div>
                    <div className="mobile-card-quantity"><span className="qty-label">Stock</span><span className="qty-value">{item.quantity}</span></div>
                </div>
                <div className="mobile-card-footer">
                    <div className="mobile-card-meta"><span className="tag">{item.category}</span><span>${(item.unitCost || 0).toFixed(2)}</span></div>
                    <button className="btn btn-secondary btn-sm" onClick={(e) => { e.stopPropagation(); setHistoryItem(item); setIsHistoryOpen(true); }}><FiClock/></button>
                </div>
            </div>
        );
    };

    return (
        <>
            <AddItemModal isOpen={isAddItemModalOpen} onClose={() => setAddItemModalOpen(false)} onAddItem={onAddItem} masterInventoryItems={masterInventoryItems} allItems={allItems} ownerId={owner._id} ownerType={ownerType} />
            <InventoryUnitsModal isOpen={isUnitsModalOpen} onClose={() => setIsUnitsModalOpen(false)} item={selectedItem} masterInventoryItems={masterInventoryItems} />
            <InventoryDetailsModal isOpen={isDetailsModalOpen} onClose={() => setIsDetailsModalOpen(false)} item={selectedItem} allItems={allItems} masterInventoryItems={masterInventoryItems} canDelete={canDeleteInventory} 
                onAction={(m) => { setActionModalMode(m); setIsDetailsModalOpen(false); setIsActionModalOpen(true); }} 
                onHistory={() => { setHistoryItem(selectedItem); setIsDetailsModalOpen(false); setIsHistoryOpen(true); }} 
                onAdjust={() => { setItemToAdjustSku(selectedItem.sku || selectedItem._id); setIsDetailsModalOpen(false); setAdjustStockModalOpen(true); }}
                onViewSubItemUnits={(s) => { setSelectedItem(s); setIsDetailsModalOpen(false); setIsUnitsModalOpen(true); }}
                onDelete={() => handleDeleteSingleItem(selectedItem)}
            />
            <AdjustStockModal isOpen={isAdjustStockModalOpen} onClose={() => setAdjustStockModalOpen(false)} onAdjustStock={onAdjustStock} items={enrichedOwnerItems} initialItemSku={itemToAdjustSku} />
            <InventoryHistoryModal isOpen={isHistoryOpen} onClose={() => setIsHistoryOpen(false)} item={historyItem} />
            <UnitHistoryModal isOpen={isUnitHistoryOpen} onClose={() => setIsUnitHistoryOpen(false)} serialNumber={unitHistorySN} />
            <InventoryActionModal isOpen={isActionModalOpen} onClose={() => { setIsActionModalOpen(false); setActionModalMode('menu'); }} item={selectedItem} sites={sites} communities={communities} tickets={tickets} onAdjustStock={onAdjustStock} onAddItem={onAddItem} onTransferStock={onTransferStock} onConsumeItem={onConsumeItem} initialMode={actionModalMode} />
            <ScanQRModal isOpen={isScannerOpen} onClose={() => setIsScannerOpen(false)} onScan={handleScan} />

            <div className="page-header" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '1rem' }}>
                <div className="header-left-details">
                    <button onClick={onBack} className="back-btn"><FiArrowLeft /></button>
                    <div><h2 className="page-title">{owner.name} Inventory</h2><span className="page-subtitle">{ownerType} Stock</span></div>
                </div>
                <div className="page-actions" style={{ width: isMobile ? '100%' : 'auto' }}>
                    {isMobile && <button className="btn btn-secondary" onClick={() => setIsScannerOpen(true)} style={{ width: '100%', marginBottom: '0.5rem' }}><FiCamera className="btn-icon" /> Scan QR</button>}
                    {owner.type === 'Office' && canManageInventory && <button className="btn btn-primary" onClick={() => setAddItemModalOpen(true)} style={{ width: '100%' }}><FiPlus className="btn-icon" /> Add New Item</button>}
                </div>
            </div>

            <div className={isMobile ? "" : "table-card"}>
                <div className="table-controls" style={isMobile ? { background: 'transparent', border: 'none', padding: '0 0 1rem 0' } : {}}>
                    <div className="search-input-wrapper"><FiSearch className="search-icon" /><input type="text" placeholder="Search items..." className="search-input" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} /></div>
                    <div className="filters-group">
                        <select className="filter-select" value={filters.category} onChange={e => setFilters({...filters, category: e.target.value})}><option>All Categories</option><option>Parts</option><option>Tools</option><option>Filters</option><option>Assembly</option></select>
                        <button className={`btn ${filters.lowStockOnly ? 'btn-danger' : 'btn-secondary'}`} onClick={() => setFilters({...filters, lowStockOnly: !filters.lowStockOnly})}><FiAlertTriangle className="btn-icon" />Low Stock</button>
                    </div>
                </div>

                {isMobile ? (
                    <div className="mobile-inventory-list">
                        {filteredItems.slice((currentPage-1)*rowsPerPage, currentPage*rowsPerPage).map(item => renderMobileCard(item))}
                    </div>
                ) : (
                    <div className="table-wrapper">
                        <table>
                            <thead><tr><th>Image</th><th>Item Name</th><th>Manufacturer</th><th>SKU</th><th>Category</th><th>Quantity</th><th>Unit Cost</th><th>Last Updated</th><th style={{textAlign:'right'}}>Actions</th></tr></thead>
                            <tbody className="table-animated-rows">
                                {filteredItems.slice((currentPage-1)*rowsPerPage, currentPage*rowsPerPage).map(item => (
                                    <tr key={item._id} className="clickable-row" onClick={() => handleItemClick(item)}>
                                        <td><div className="list-view-image-thumbnail-wrapper">{item.imageUrl ? <img src={item.imageUrl} alt="" className="list-view-image-thumbnail" /> : <FiPackage className="list-view-image-placeholder-icon" />}</div></td>
                                        <td>{item.name}</td><td>{item.manufacturer || '-'}</td><td>{item.sku}</td><td>{item.category}</td><td>{item.quantity}</td><td>${(item.unitCost || 0).toFixed(2)}</td><td>{new Date(item.lastUpdated).toLocaleDateString()}</td>
                                        <td style={{textAlign:'right'}}>
                                            <button className="btn btn-secondary btn-sm" onClick={(e) => { e.stopPropagation(); setHistoryItem(item); setIsHistoryOpen(true); }}><FiClock /></button>
                                            {canDeleteInventory && <button className="btn-icon-only btn-icon-danger" onClick={(e) => { e.stopPropagation(); handleDeleteSingleItem(item); }} style={{ marginLeft: '0.5rem' }}><FiTrash2 size={16} /></button>}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                <div className="table-footer">
                    <div className="pagination-info"><span>{filteredItems.length > 0 ? `${(currentPage-1)*rowsPerPage+1}-${Math.min(currentPage*rowsPerPage, filteredItems.length)} of ${filteredItems.length}` : '0 of 0'}</span></div>
                    <div className="pagination-controls">
                        <button onClick={() => setCurrentPage(p => p - 1)} disabled={currentPage === 1}><FiChevronLeft /></button>
                        <button onClick={() => setCurrentPage(p => p + 1)} disabled={currentPage >= Math.ceil(filteredItems.length / rowsPerPage) || filteredItems.length === 0}><FiChevronRight /></button>
                    </div>
                </div>
            </div>
        </>
    );
};

export default InventoryListView;